import type { DocPage } from '../types'
import { media } from '../media'


export const automap: DocPage = {
  id: 'automap',
  title: 'Auto Map',
  description:
    'Use the separate Auto Map window with Strict, Balanced, or Exploratory strategies, trust levels, filters, fill-unmapped-only, and selected-collections scope.',
  category: 'Mapping',
  sections: [
    { id: 'window', title: 'Separate window' },
    { id: 'strategies', title: 'Strategies' },
    { id: 'trust', title: 'Trust levels' },
    { id: 'filters', title: 'Filters and options' },
    { id: 'scope', title: 'Selected collections scope' },
    { id: 'apply', title: 'Apply suggestions' },
    { id: 'workflow', title: 'Recommended workflow' },
  ],
  blocks: [
    {
      type: 'heading',
      level: 2,
      id: 'window',
      text: 'Separate window',
    },
    {
      type: 'paragraph',
      text: 'Click Auto Map on the vertical rail to open a dedicated window titled Auto Map or Auto Map — Selected Collections when you pre-selected collection pairs. Work in that window, then apply accepted suggestions back into the Mapping panel.',
    },
    {
      type: 'figure',
      src: media.autoMapModal,
      alt: 'Auto Map window with strategies and suggestions',
      caption:
        'Figure: Auto Map window — strategy (Strict / Balanced / Exploratory), filters, trust levels, and suggested attribute mappings to apply.',
    },
    {
      type: 'heading',
      level: 2,
      id: 'strategies',
      text: 'Strategies',
    },
    {
      type: 'table',
      headers: ['Strategy', 'Behavior'],
      rows: [
        [
          'Strict',
          'Exact / normalized name matches only — fewest false positives.',
        ],
        [
          'Balanced',
          'Default. Recommended. Strong matches plus weaker name hints for review.',
        ],
        [
          'Exploratory',
          'Broadest matching, including Low / Very Low trust suggestions for manual review.',
        ],
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'trust',
      text: 'Trust levels',
    },
    {
      type: 'table',
      headers: ['Trust', 'Typical group'],
      rows: [
        ['Very High', 'Ready to apply'],
        ['High', 'Ready to apply'],
        ['Moderate', 'Needs your review'],
        ['Low', 'Needs your review (common in Exploratory)'],
        ['Very Low', 'Needs your review (Exploratory)'],
      ],
    },
    {
      type: 'paragraph',
      text: 'Suggestions are grouped into Ready to apply and Needs your review so you can accept high-confidence links quickly and inspect weaker ones.',
    },
    {
      type: 'heading',
      level: 2,
      id: 'filters',
      text: 'Filters and options',
    },
    {
      type: 'table',
      headers: ['Option', 'Purpose'],
      rows: [
        ['Scope / Filters', 'Section containing matching constraints.'],
        ['Exact', 'Prefer exact name equality.'],
        ['Fuzzy & normalized', 'Allow normalized/fuzzy name similarity.'],
        ['Require compatible types', 'Only suggest when attribute types are compatible.'],
        ['Substring & identifier', 'Allow substring / identifier-style matches.'],
        [
          'Fill unmapped source attributes only',
          'Do not propose replacements for attributes that already have mappings.',
        ],
        ['Min fuzzy / match confidence', 'Raise the floor for weaker strategies.'],
        ['Collection vs attribute filter sections', 'Limit which levels are considered.'],
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'scope',
      text: 'Selected collections scope',
    },
    {
      type: 'list',
      items: [
        'Pre-select source/target collection pairs in the trees before opening Auto Map to lock scope.',
        'The window may show a locked pair hint and User selected by you.',
        'Use Add another collection pair to expand scope without closing the window.',
        'Title becomes Auto Map — Selected Collections when scoped.',
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'apply',
      text: 'Apply suggestions',
    },
    {
      type: 'steps',
      items: [
        'Review Ready to apply and Needs your review groups.',
        'Deselect any incorrect suggestions.',
        'Click Apply {{count}} (or the equivalent Apply action with the selected count).',
        'Return to the Mapping panel and verify Connect-ID / value mappings still make sense.',
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'workflow',
      text: 'Recommended workflow',
    },
    {
      type: 'steps',
      items: [
        'Connect collections manually (or accept high-level Auto Map collection pairs if offered).',
        'Open Auto Map with Balanced and Fill unmapped source attributes only enabled.',
        'Apply Very High / High suggestions.',
        'Switch to Exploratory only if many fields remain unmapped, then review Low / Very Low carefully.',
        'Finish with fixed mappings, value mappings, and Connect-ID as needed.',
      ],
    },
  ],
}
